/**
 * DEC-329 — dispatcher-completion approval requests (migration 0154; quick 261013-dca).
 * Spec: docs/CLAUDE.md §44 / §46.
 *
 * Under SUPERVISOR_REQUIRED, a plain dispatcher's "Send to review" (DEC-321) becomes one of
 * these rows; a manager approves (the force then runs) or declines (the job stays stuck).
 * At most ONE pending request per job (partial unique index). A pending request whose job
 * has left the stuck list is CANCELLED with cancel_reason JOB_LEFT_STUCK_LIST.
 *
 * Tenant-scoped under RLS (USING + WITH CHECK on app.current_tenant_id).
 */
import {
  check,
  index,
  pgPolicy,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { appUserRole } from './enums.js';
import { tenants, users } from './users.js';
import { jobs } from './jobs.js';

export const DISPATCHER_COMPLETION_REQUEST_STATUSES = [
  'PENDING',
  'APPROVED',
  'DECLINED',
  'CANCELLED',
] as const;
export type DispatcherCompletionRequestStatus =
  (typeof DISPATCHER_COMPLETION_REQUEST_STATUSES)[number];

const tenantIsolation = sql`tenant_id::text = current_setting('app.current_tenant_id', true)`;

export const dispatcherCompletionRequests = pgTable(
  'dispatcher_completion_requests',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    jobId: uuid('job_id')
      .notNull()
      .references(() => jobs.id, { onDelete: 'cascade' }),
    requestedBy: uuid('requested_by').references(() => users.id, { onDelete: 'set null' }),
    requestedAt: timestamp('requested_at', { withTimezone: true }).notNull().defaultNow(),
    reason: text('reason').notNull(),
    status: text('status').$type<DispatcherCompletionRequestStatus>().notNull().default('PENDING'),
    decidedBy: uuid('decided_by').references(() => users.id, { onDelete: 'set null' }),
    decidedAt: timestamp('decided_at', { withTimezone: true }),
    decisionNote: text('decision_note'),
    cancelReason: text('cancel_reason').$type<'JOB_LEFT_STUCK_LIST'>(),
  },
  (t) => [
    uniqueIndex('dispatcher_completion_requests_one_pending_per_job')
      .on(t.jobId)
      .where(sql`${t.status} = 'PENDING'`),
    index('dispatcher_completion_requests_tenant_status_idx').on(
      t.tenantId,
      t.status,
      t.requestedAt,
    ),
    check('dispatcher_completion_requests_reason_length', sql`length(btrim(${t.reason})) >= 8`),
    check(
      'dispatcher_completion_requests_status_check',
      sql`${t.status} IN ('PENDING', 'APPROVED', 'DECLINED', 'CANCELLED')`,
    ),
    check(
      'dispatcher_completion_requests_decided_pair',
      sql`(${t.status} = 'PENDING') = (${t.decidedAt} IS NULL)`,
    ),
    check(
      'dispatcher_completion_requests_decline_note',
      sql`${t.status} <> 'DECLINED' OR length(btrim(COALESCE(${t.decisionNote}, ''))) >= 8`,
    ),
    check(
      'dispatcher_completion_requests_cancel_reason',
      sql`(${t.status} = 'CANCELLED') = (${t.cancelReason} IS NOT NULL)`,
    ),
    check(
      'dispatcher_completion_requests_cancel_reason_check',
      sql`${t.cancelReason} IS NULL OR ${t.cancelReason} IN ('JOB_LEFT_STUCK_LIST')`,
    ),
    pgPolicy('dispatcher_completion_requests_tenant_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: tenantIsolation,
      withCheck: tenantIsolation,
    }),
  ],
);

export type DispatcherCompletionRequestRow = typeof dispatcherCompletionRequests.$inferSelect;
